import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiFetch } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { clockTime } from "@/lib/format";
import { Lock, Send, LogOut } from "lucide-react";

export default function Banned() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [text, setText] = useState("");
  const scrollRef = useRef(null);

  const { data: convs } = useQuery({
    queryKey: ["banned-convs"],
    queryFn: () => apiFetch("/conversations"),
    refetchInterval: 15000,
  });
  const conv = convs?.[0];

  const { data: messages } = useQuery({
    queryKey: ["banned-messages", conv?.id],
    queryFn: () => apiFetch(`/conversations/${conv.id}/messages`),
    enabled: !!conv?.id,
    refetchInterval: 8000,
  });

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [messages]);

  const send = async () => {
    const t = text.trim();
    if (!t) return;
    setText("");
    try {
      await apiFetch("/support/contact-team", { method: "POST", body: JSON.stringify({ message: t }) });
      qc.invalidateQueries({ queryKey: ["banned-convs"] });
      qc.invalidateQueries({ queryKey: ["banned-messages", conv?.id] });
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <div className="border-b border-border bg-brand px-4 py-4">
        <div className="mx-auto flex max-w-2xl items-center justify-between">
          <div className="flex items-center gap-2 text-white">
            <Lock className="h-5 w-5" />
            <span className="font-display font-bold">Account suspended</span>
          </div>
          <button data-testid="banned-logout" onClick={() => { logout(); navigate("/login"); }} className="flex items-center gap-1.5 text-sm text-white/80 hover:text-white">
            <LogOut className="h-4 w-4" /> Log out
          </button>
        </div>
      </div>

      <div className="mx-auto flex w-full max-w-2xl flex-1 flex-col px-4 py-6">
        <div className="rounded-2xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
          <p className="font-semibold">Your account has been suspended.</p>
          {user?.ban_reason ? (
            <p className="mt-1">Reason: {user.ban_reason}</p>
          ) : (
            <p className="mt-1">You can message the Venda Team below to appeal.</p>
          )}
        </div>

        <h2 className="mt-6 font-display font-bold text-foreground">Venda Team</h2>
        <div ref={scrollRef} className="mt-2 flex-1 space-y-3 overflow-y-auto rounded-2xl border border-border bg-white p-4" data-testid="banned-chat" style={{ maxHeight: "50vh" }}>
          {(!messages || messages.length === 0) && (
            <p className="py-8 text-center text-sm text-muted-foreground">No messages yet. Send a message to appeal your suspension.</p>
          )}
          {messages?.map((m) => {
            const mine = m.sender_id === user?.id;
            return (
              <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[75%] rounded-2xl px-3.5 py-2 text-sm ${mine ? "bg-price text-white" : "bg-secondary text-foreground"}`}>
                  <p>{m.text}</p>
                  <p className={`mt-0.5 text-[10px] ${mine ? "text-white/70" : "text-muted-foreground"}`}>{clockTime(m.created_at)}</p>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-3 flex items-center gap-2">
          <input
            data-testid="banned-input"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && send()}
            placeholder="Message the Venda Team…"
            className="flex-1 rounded-full border border-input bg-white px-4 py-2.5 text-sm outline-none focus:border-price"
          />
          <button data-testid="banned-send" onClick={send} className="flex h-11 w-11 items-center justify-center rounded-full bg-price text-white">
            <Send className="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  );
}
